import { Checkbox, Flex, Form, notification, Tooltip } from "antd";
import { type FC, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { patchMarketMakeArchived } from "tm-shared/api";

import BaseButton from "@/components/BaseButton";
import { BaseModal } from "@/components/BaseModal";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch } from "@/hooks/redux";
import {
    setAddressVerifyModalSelectionPayload,
    setMarketCreatePageModalSelectionPayload,
} from "@/store/slices/ui";
import type { DuplicateByAddressModalPayload } from "@/types/modals";

import type { MarketRelationsFormValues } from "./DuplicateByAddressModal.types";
import { buildMarketChains } from "./DuplicateByAddressModal.utilities";
import { MarketRelationRow } from "./MarketRelationRow";
import { TooltipTextComponent } from "./TooltipTextComponent";

interface DuplicateByAddressModalProps {
    onClose: () => void;
    payload: DuplicateByAddressModalPayload;
}

export const DuplicateByAddressModal: FC<DuplicateByAddressModalProps> = ({
    onClose,
    payload,
}) => {
    const { address, markets } = payload;

    const dispatch = useAppDispatch();

    const [checkConfirm, setCheckConfirm] = useState(false);

    const methods = useForm<MarketRelationsFormValues>({
        defaultValues: {},
    });

    const {
        formState: { isSubmitting },
    } = methods;

    const formValues = useWatch({ control: methods.control });

    const parentId = Object.entries(formValues).find(
        ([_marketField, value]) => value === "parent",
    );

    const hasParentSelected = Boolean(parentId);

    const onSubmit = async (data: MarketRelationsFormValues) => {
        if (parentId) {
            if (payload.variant === "verifyAddress") {
                dispatch(
                    setAddressVerifyModalSelectionPayload({
                        value: parentId[0],
                        fieldName: "parentId",
                    }),
                );
            } else {
                dispatch(
                    setMarketCreatePageModalSelectionPayload({
                        value: parentId[0],
                        fieldName: "parentId",
                    }),
                );
            }
        }

        const archivedMarkets = Object.entries(data).reduce(
            (accumulator, [marketId, value]) => {
                if (value === "archived") {
                    accumulator.push(marketId);
                }

                return accumulator;
            },
            [] as string[],
        );

        if (archivedMarkets.length > 0) {
            const results = await Promise.allSettled(
                archivedMarkets.map((marketId) =>
                    patchMarketMakeArchived({ marketId }),
                ),
            );

            const failedMarketIds = results.flatMap((result, index) =>
                result.status === "rejected" ? [archivedMarkets[index]] : [],
            );

            if (failedMarketIds.length > 0) {
                console.error("Не вдалося заархівувати ТТ:", failedMarketIds);

                notification.error({
                    message: `Не вдалося заархівувати ${failedMarketIds.length} ТТ`,
                });
            }
        }

        onClose();
    };

    const marketsTree = buildMarketChains(markets);

    return (
        <BaseModal
            open
            onClose={onClose}
            maskClosable={false}
            actions={null}
            title={"Увага"}
            closable={false}
            styles={{
                content: {
                    maxHeight: "95vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    padding: "20px 0",
                },
                body: {
                    height: "100%",
                    overflowY: "auto",
                    padding: "0 24px",
                },
            }}
        >
            <FormProvider {...methods}>
                <Form
                    onFinish={methods.handleSubmit(onSubmit)}
                    layout="vertical"
                >
                    <Flex vertical gap={16}>
                        <PrimaryText>
                            За адресою {address} знайдено торгові точки
                        </PrimaryText>

                        <Flex
                            vertical
                            gap={12}
                            style={{ overflow: "auto", minHeight: 0 }}
                        >
                            {marketsTree.map((market) => (
                                <MarketRelationRow
                                    key={market.id}
                                    market={market}
                                    level={0}
                                    formValues={formValues}
                                    hasParentSelected={hasParentSelected}
                                    setValue={(name, value) =>
                                        methods.setValue(name, value, {
                                            shouldDirty: true,
                                        })
                                    }
                                />
                            ))}
                        </Flex>

                        <Flex vertical gap={16}>
                            <Checkbox
                                checked={checkConfirm}
                                disabled={isSubmitting}
                                onChange={(evt) =>
                                    setCheckConfirm(evt.target.checked)
                                }
                            >
                                <Flex gap={4}>
                                    <PrimaryText>Я розумію</PrimaryText>

                                    <Tooltip title={<TooltipTextComponent />}>
                                        <PrimaryText
                                            $textColor="accent"
                                            fontWeight="500"
                                            style={{
                                                textDecoration: "underline",
                                            }}
                                        >
                                            наслідки
                                        </PrimaryText>
                                    </Tooltip>

                                    <PrimaryText>цієї дії</PrimaryText>
                                </Flex>
                            </Checkbox>

                            <BaseButton
                                type="default"
                                size="large"
                                htmlType="submit"
                                disabled={!checkConfirm}
                                loading={isSubmitting}
                            >
                                Ok
                            </BaseButton>
                        </Flex>
                    </Flex>
                </Form>
            </FormProvider>
        </BaseModal>
    );
};
